import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Eye, Users, FileText, BookOpen, AlertTriangle, Sparkles, TrendingUp } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

const API_URL = window.location.origin || '';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35 } } };

const KPI = ({ label, value, icon: Icon, color }) => (
  <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
    <div className="p-2 rounded-xl w-fit mb-2" style={{ background: `${color}12` }}><Icon className="h-5 w-5" style={{ color }} /></div>
    <p className="text-2xl font-bold text-slate-800">{value}</p>
    <p className="text-xs text-slate-500">{label}</p>
  </motion.div>
);

export default function CoordinationDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiSummary, setAiSummary] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/infantil360/coordination/dashboard?year=2026&trimester=${encodeURIComponent('1º Trimestre')}`, { headers: headers() })
      .then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleAISummary = async () => {
    setGenerating(true);
    try {
      const context = JSON.stringify(data?.kpis || {}) + '\nTurmas: ' + (data?.class_ranking || []).map(c => `${c.name}: ${c.observations} obs`).join(', ') + '\nAlertas: ' + (data?.alerts || []).map(a => a.message).join(', ');
      const res = await fetch(`${API_URL}/api/infantil360/ai/insight`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ context })
      }).then(r => r.json());
      if (res.ok) { setAiSummary(res.summary); toast.success('Resumo gerado!'); }
    } catch { toast.error('Erro'); }
    finally { setGenerating(false); }
  };

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-20 rounded-2xl bg-slate-100" /><div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-28 rounded-2xl bg-slate-100" />)}</div></div>;

  const kpis = data?.kpis || {};
  const alerts = data?.alerts || [];

  return (
    <motion.div className="space-y-6" initial="hidden" animate="visible" variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.05 } } }}
      data-testid="coordination-page">
      <motion.div variants={fadeUp} className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><Shield className="h-5 w-5 text-[#64327D]" /> Coordenação 360</h1>
          <p className="text-sm text-slate-500 mt-1">Painel executivo — 1º Trimestre 2026</p>
        </div>
        <Button onClick={handleAISummary} disabled={generating} className="rounded-xl" style={{ background: 'linear-gradient(135deg, #64327D, #8E52AB)' }}>
          <Sparkles className="h-4 w-4 mr-2" /> {generating ? 'Gerando...' : 'Resumo Executivo IA'}
        </Button>
      </motion.div>

      <motion.div className="grid grid-cols-2 md:grid-cols-5 gap-4" variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.04 } } }}>
        <KPI label="Observações" value={kpis.total_observations || 0} icon={Eye} color="#64327D" />
        <KPI label="Turmas" value={kpis.total_classes || 0} icon={Users} color="#3B82F6" />
        <KPI label="Portfólios" value={kpis.total_portfolios || 0} icon={BookOpen} color="#F59E0B" />
        <KPI label="Relatórios" value={kpis.total_reports || 0} icon={FileText} color="#EC4899" />
        <KPI label="Pendentes" value={kpis.pending_reports || 0} icon={AlertTriangle} color="#EF4444" />
      </motion.div>

      {/* AI Summary */}
      {aiSummary && (
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-[#5B3B8C20] shadow-sm">
          <h3 className="text-sm font-bold text-[#64327D] mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4" /> Resumo Executivo IA</h3>
          <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">{aiSummary}</div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Class Ranking */}
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#2BB673]" /> Ranking de Turmas</h3>
          {(data?.class_ranking || []).length === 0 ? <p className="text-xs text-slate-400 text-center py-4">Sem dados</p> : (
            <div className="space-y-2">
              {(data?.class_ranking || []).map((c, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                  <span className="w-6 h-6 rounded-full bg-[#64327D] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <span className="text-sm font-medium text-slate-700 flex-1">{c.name}</span>
                  <span className="text-sm font-bold text-[#64327D]">{c.observations}</span>
                  <span className="text-xs text-slate-400">obs</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Alerts */}
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Alertas</h3>
          {alerts.length === 0 ? (
            <p className="text-xs text-emerald-600 text-center py-4">Nenhum alerta ativo!</p>
          ) : (
            <div className="space-y-2">
              {alerts.map((a, i) => (
                <div key={i} className={`flex items-start gap-2 p-3 rounded-xl ${a.type === 'critico' ? 'bg-red-50 border border-red-100' : 'bg-amber-50 border border-amber-100'}`}>
                  <AlertTriangle className={`h-4 w-4 flex-shrink-0 mt-0.5 ${a.type === 'critico' ? 'text-red-400' : 'text-amber-400'}`} />
                  <span className={`text-xs ${a.type === 'critico' ? 'text-red-700' : 'text-amber-700'}`}>{a.message}</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}
